import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Col,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Tag,
} from "antd";

// Constants
import { PAGE, PAGE_NAME } from "@utils/constants";

// Layouts
import StickyBreadcrumbPageComponent from "@layouts/stickyBreadcrumbPage/stickyBreadcrumbPage";

// APIs
import {
  cleanCustomFields,
  deleteRecord,
  getRecord,
  listCustomFieldDefs,
  updateRecord,
} from "@apis/erent2Application";

const BREADCRUMB = [
  { name: PAGE_NAME.ERENT2_APPLICATION_LIST, path: PAGE.ERENT2_APPLICATION_LIST },
  { name: PAGE_NAME.ERENT2_APPLICATION_DETAIL, path: "#" },
];

const REGION_OPTIONS = [
  { value: "central-and-western", label: "中西區" },
  { value: "eastern", label: "東區" },
  { value: "southern", label: "南區" },
  { value: "wan-chai", label: "灣仔區" },
  { value: "kowloon-city", label: "九龍城區" },
  { value: "kwun-tong", label: "觀塘區" },
  { value: "sham-shui-po", label: "深水埗區" },
  { value: "wong-tai-sin", label: "黃大仙區" },
  { value: "yau-tsim-mong", label: "油尖旺區" },
  { value: "islands", label: "離島區" },
  { value: "kwai-tsing", label: "葵青區" },
  { value: "north", label: "北區" },
  { value: "sai-kung", label: "西貢區" },
  { value: "sha-tin", label: "沙田區" },
  { value: "tai-po", label: "大埔區" },
  { value: "tsuen-wan", label: "荃灣區" },
  { value: "tuen-mun", label: "屯門區" },
  { value: "yuen-long", label: "元朗區" },
];

// ponytail: group is read-only — derived from region by the server, never sent on PUT.
const GROUP_LABEL = { A: "A 組", B: "B 組", C: "C 組" };

const EMPTY_FORM = {
  fullName: "",
  phoneNumber: "",
  email: "",
  region: undefined,
  interestedProduct: "",
  remarks: "",
  customFields: {},
};

function Erent2ApplicationDetailPage() {
  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [group, setGroup] = useState(undefined);
  const [definitions, setDefinitions] = useState([]);

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const [data, definitionsResponse] = await Promise.all([getRecord(id), listCustomFieldDefs()]);
      setDefinitions(definitionsResponse.data || definitionsResponse || []);
      setFormValues({
        fullName: data?.fullName ?? "",
        phoneNumber: data?.phoneNumber ?? "",
        email: data?.email ?? "",
        region: data?.region ?? undefined,
        interestedProduct: data?.interestedProduct ?? "",
        remarks: data?.remarks ?? "",
        customFields: data?.customFields ?? {},
      });
      setGroup(data?.group);
    } catch {
      notification.error({ message: "載入申請詳情失敗，請重試" });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  const setField = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const data = await updateRecord(id, { ...formValues, customFields: cleanCustomFields(formValues.customFields) });
      if (data?.group) setGroup(data.group);
      notification.success({ message: "已儲存" });
    } catch (error) {
      const details = error.response?.data?.details;
      notification.error({ message: "儲存失敗", description: details?.join("；") || error.response?.data?.error || "請重試" });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = () => {
    Modal.confirm({
      title: "確認刪除",
      content: "確定要刪除此申請記錄？此操作不可復原。",
      okType: "danger",
      okText: "刪除",
      cancelText: "取消",
      onOk: async () => {
        try {
          await deleteRecord(id);
          notification.success({ message: "已刪除" });
          history.push(PAGE.ERENT2_APPLICATION_LIST);
        } catch {
          notification.error({ message: "刪除失敗，請重試" });
        }
      },
    });
  };

  return (
    <StickyBreadcrumbPageComponent breadcrumb={BREADCRUMB}>
      <div />
      <Spin spinning={loading}>
        <div style={{ padding: "16px 24px" }}>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>姓名</div>
                <Input value={formValues.fullName} onChange={setField("fullName")} />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>電話</div>
                <Input value={formValues.phoneNumber} onChange={setField("phoneNumber")} />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>電郵</div>
                <Input value={formValues.email} onChange={setField("email")} />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>地區</div>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  value={formValues.region}
                  onChange={setField("region")}
                >
                  {REGION_OPTIONS.map((r) => (
                    <Select.Option key={r.value} value={r.value}>
                      {r.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>組別</div>
                <div style={{ paddingTop: 8 }}>
                  {GROUP_LABEL[group] ? (
                    <Tag color="blue">{GROUP_LABEL[group]}</Tag>
                  ) : (
                    <span style={{ color: "rgba(0,0,0,0.45)" }}>—</span>
                  )}
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>感興趣產品</div>
                <Input
                  value={formValues.interestedProduct}
                  onChange={setField("interestedProduct")}
                />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>備注</div>
                <Input.TextArea
                  rows={3}
                  value={formValues.remarks}
                  onChange={setField("remarks")}
                />
              </div>
            </Col>
            {definitions.length > 0 && <Col span={24}><h3 style={{ marginTop: 8 }}>自訂欄位</h3></Col>}
            {definitions.map((definition) => {
              const value = formValues.customFields[definition.key];
              const stale = [];
              const options = definition.options || [];
              if (definition.type === "select" && value && !options.includes(value)) stale.push(value);
              if (definition.type === "multiselect") (value || []).forEach((item) => { if (!options.includes(item)) stale.push(item); });
              const onChange = (nextValue) => setFormValues((current) => ({ ...current, customFields: { ...current.customFields, [definition.key]: nextValue } }));
              return <Col span={definition.type === "text" ? 24 : 12} key={definition._id}>
                <div style={{ marginBottom: 16 }}><div style={{ marginBottom: 4 }}>{definition.label}</div>
                  {definition.type === "text" ? <Input maxLength={500} value={value || ""} onChange={(event) => onChange(event.target.value)} /> :
                    <Select allowClear mode={definition.type === "multiselect" ? "multiple" : undefined} style={{ width: "100%" }} value={value} onChange={onChange}>
                      {stale.map((option) => <Select.Option key={option} value={option} disabled>{option}（已移除）</Select.Option>)}
                      {(definition.options || []).map((option) => <Select.Option key={option} value={option}>{option}</Select.Option>)}
                    </Select>}
                </div>
              </Col>;
            })}
          </Row>
          <Row>
            <Col>
              <Button
                type="primary"
                loading={saving}
                style={{ marginRight: 8 }}
                onClick={onSave}
              >
                儲存
              </Button>
              <Button type="danger" onClick={onDelete}>
                刪除
              </Button>
            </Col>
          </Row>
        </div>
      </Spin>
    </StickyBreadcrumbPageComponent>
  );
}

export default Erent2ApplicationDetailPage;
